import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import AlertBanner from "../../../DesignSystem/AlertBanner/AlertBanner";
import { useUserPermissions } from "../../../SignIn/UserPermissionsContext";
import { PostCard } from "../HelperFunctions/HelperFunctions";
import {
  commitIOAdviserIdImport,
  previewIOAdviserIdImport,
} from "./ioDetailsBulkUploadService";
import "../../../../css/Adviser/EditAdviserDetailsPage.css";
import "../../../../css/Adviser/IOBulkUpload.css";

const getStatusClass = (status) => {
  if (status === "Matched") return "ioImportStatus ioImportStatus--matched";
  if (status === "No change") return "ioImportStatus ioImportStatus--unchanged";
  return "ioImportStatus ioImportStatus--error";
};

const formatIds = (value) => {
  const values = Array.isArray(value) ? value : [value];
  const populated = values.filter((entry) => entry !== "" && entry != null);
  return populated.length ? populated.join(", ") : "-";
};

export default function IOBulkUploadPage() {
  const { userPermissions } = useUserPermissions();
  const canImport = userPermissions.some((permission) =>
    ["IO Admin", "Administrator"].includes(permission),
  );
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analysing, setAnalysing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const canCommit = useMemo(
    () => preview?.valid && preview?.matchedRows?.length > 0,
    [preview],
  );

  const handleAnalyse = async () => {
    try {
      setAnalysing(true);
      setError("");
      setSuccessMessage("");
      setPreview(null);
      setPreview(await previewIOAdviserIdImport(file));
    } catch (analyseError) {
      console.error("Intelliflo identifier spreadsheet analysis failed:", analyseError);
      setError(analyseError.message || "Spreadsheet analysis failed.");
    } finally {
      setAnalysing(false);
    }
  };

  const handleImport = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");
      const result = await commitIOAdviserIdImport(preview);
      setSuccessMessage(
                `${result.written} adviser identifier record${result.written === 1 ? "" : "s"} added to Firebase successfully.`,
      );
      setPreview((current) => ({
        ...current,
        matchedRows: [],
        noChangeCount: current.totalRows,
        rows: current.rows.map((row) =>
          row.status === "Matched"
            ? {
                ...row,
                status: "No change",
                currentIdentifiers: row.identifiers,
                reason: "Saved in Firebase.",
              }
            : row,
        ),
      }));
    } catch (importError) {
      console.error("Intelliflo identifier import failed:", importError);
      setError(importError.message || "Import failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setPreview(null);
    setError("");
    setSuccessMessage("");
  };

  return (
    <div className="main-content">
      <Helmet>
        <title>Intelliflo Identifier Upload</title>
      </Helmet>
      <Sidebar />

      <div className="Main-Container">
        <div className="smFeed">
          <Breadcrumb
            items={[
              { label: "Intelliflo", to: "/Department/Intelliflo" },
              { label: "Identifier Upload" },
            ]}
          />

          {!canImport ? (
            <AlertBanner
              type="error"
              message="You need IO Admin or Administrator permission to import Intelliflo ID details."
              showClose={false}
            />
          ) : (
            <>
              <div className="smEditActions">
                <div>
                  <h3 className="FP_H3">Intelliflo Identifier Upload</h3>
                  <p className="Content-Small muted">
                    Analyse the Excel file, review every email match, then add
                    the confirmed identifiers to each adviser's IO Details
                    record in one click.
                  </p>
                </div>
              </div>

              {error ? (
                <AlertBanner
                  type="error"
                  message={error}
                  onClose={() => setError("")}
                />
              ) : null}
              {successMessage ? (
                <AlertBanner
                  type="success"
                  message={successMessage}
                  onClose={() => setSuccessMessage("")}
                />
              ) : null}

              <PostCard title="Upload Excel file">
                <p className="Content-Small muted">
                  Required columns: email, id, group.id and user.id. The
                  Intelliflo adviser export is supported directly.
                </p>
                <div className="smImportFileBox">
                  <label className="FP_Content_Semibold" htmlFor="io-id-import">
                    Excel file
                  </label>
                  <input
                    id="io-id-import"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(event) => {
                      setFile(event.target.files?.[0] || null);
                      setPreview(null);
                      setError("");
                      setSuccessMessage("");
                    }}
                  />
                  {file ? (
                    <div className="Content-Small muted">Selected: {file.name}</div>
                  ) : null}
                </div>
                <div className="smEditActionsRight">
                  <PrimaryButton
                    label={analysing ? "Analysing..." : "Analyse Excel"}
                    onClick={handleAnalyse}
                    disabled={!file || analysing || saving}
                  />
                </div>
              </PostCard>

              {preview ? (
                <>
                  <PostCard title="Import summary">
                    <div className="smGrid2">
                      <div className="Content-Small">
                        Workbook: {preview.fileName}
                      </div>
                      <div className="Content-Small">
                        Source sheet: {preview.sheetName}
                      </div>
                      <div className="Content-Small">
                        Spreadsheet rows: {preview.totalRows}
                      </div>
                      <div className="Content-Small">
                        Ready to add: {preview.matchedRows.length}
                      </div>
                      <div className="Content-Small">
                        Already correct: {preview.noChangeCount}
                      </div>
                      <div className="Content-Small">
                        Issues: {preview.invalidCount}
                      </div>
                    </div>
                    {!preview.valid ? (
                      <div className="ioImportNotice ioImportNotice--error Content-Small">
                        Nothing can be added until every invalid, duplicate or
                        unmatched row is corrected in the spreadsheet.
                      </div>
                    ) : (
                      <div className="ioImportNotice ioImportNotice--success Content-Small">
                        Every spreadsheet row matches an existing adviser and
                        has valid integer values for id, group.id and user.id.
                      </div>
                    )}
                  </PostCard>

                  <PostCard title="Review email mapping">
                    <div className="ioImportTableWrap">
                      <table className="ioImportTable">
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Adviser name</th>
                            <th>Spreadsheet email</th>
                            <th>Existing adviser email</th>
                            <th>Existing IO Adviser ID</th>
                            <th>Existing adviser ID</th>
                            <th>Current id</th>
                            <th>Uploaded id</th>
                            <th>Current group.id</th>
                            <th>Uploaded group.id</th>
                            <th>Current user.id</th>
                            <th>Uploaded user.id</th>
                            <th>Status</th>
                            <th>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.rows.map((row) => (
                            <tr key={`${row.rowNumber}-${row.email}`}>
                              <td>{row.rowNumber}</td>
                              <td>{row.fullName || "-"}</td>
                              <td>{row.email || "-"}</td>
                              <td>{row.storedEmail || "-"}</td>
                              <td>{formatIds(row.existingIoAdviserId)}</td>
                              <td>{row.existingAdviserId || "-"}</td>
                              <td>{formatIds(row.currentIdentifiers?.id)}</td>
                              <td>{row.identifiers?.id ?? "-"}</td>
                              <td>{formatIds(row.currentIdentifiers?.groupId)}</td>
                              <td>{row.identifiers?.groupId ?? "-"}</td>
                              <td>{formatIds(row.currentIdentifiers?.userId)}</td>
                              <td>{row.identifiers?.userId ?? "-"}</td>
                              <td>
                                <span className={getStatusClass(row.status)}>
                                  {row.status}
                                </span>
                              </td>
                              <td>{row.reason || "Ready to add"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </PostCard>

                  <PostCard title="Add to IO Details">
                    <p className="Content-Small muted">
                      This merges the reviewed id, group.id and user.id values
                      into the matched adviser's IO Details record and creates
                      an edit-log entry for each changed adviser.
                    </p>
                    <div className="smEditActionsRight">
                      <SecondaryButton
                        label="Clear"
                        onClick={handleClear}
                        disabled={analysing || saving}
                      />
                      <PrimaryButton
                        label={saving ? "Adding..." : "Add to IO Details"}
                        onClick={handleImport}
                        disabled={!canCommit || analysing || saving}
                      />
                    </div>
                  </PostCard>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
